import { create } from 'zustand';

interface SettingsFormState {
  readonly language: string;
  readonly theme: string;
  readonly defaultCheckpoint: string | null;
  readonly defaultSampler: string;
  readonly defaultScheduler: string;
  readonly defaultSteps: number;
  readonly defaultCfgScale: number;
  readonly defaultWidth: number;
  readonly defaultHeight: number;
  readonly defaultNegativePrompt: string;
  readonly defaultPagEnabled: boolean;
  readonly defaultPagScale: number;
  readonly defaultFreeUEnabled: boolean;
  readonly defaultFreeUB1: number;
  readonly defaultFreeUB2: number;
  readonly defaultFreeUS1: number;
  readonly defaultFreeUS2: number;
  readonly isDirty: boolean;

  setLanguage: (language: string) => void;
  setTheme: (theme: string) => void;
  setDefaultCheckpoint: (checkpoint: string | null) => void;
  setDefaultSampler: (sampler: string) => void;
  setDefaultScheduler: (scheduler: string) => void;
  setDefaultSteps: (steps: number) => void;
  setDefaultCfgScale: (cfgScale: number) => void;
  setDefaultWidth: (width: number) => void;
  setDefaultHeight: (height: number) => void;
  setDefaultNegativePrompt: (prompt: string) => void;
  setDefaultPagEnabled: (enabled: boolean) => void;
  setDefaultPagScale: (scale: number) => void;
  setDefaultFreeUEnabled: (enabled: boolean) => void;
  setDefaultFreeUB1: (b1: number) => void;
  setDefaultFreeUB2: (b2: number) => void;
  setDefaultFreeUS1: (s1: number) => void;
  setDefaultFreeUS2: (s2: number) => void;
  loadFromServer: (settings: {
    language: string;
    theme: string;
    default_checkpoint: string | null;
    default_sampler: string;
    default_scheduler: string;
    default_steps: number;
    default_cfg_scale: number;
    default_width: number;
    default_height: number;
    default_negative_prompt: string;
    default_pag?: { enabled: boolean; scale: number };
    default_freeu?: { enabled: boolean; b1: number; b2: number; s1: number; s2: number };
  }) => void;
  markClean: () => void;
}

export const useSettingsFormStore = create<SettingsFormState>((set) => ({
  language: 'en',
  theme: 'system',
  defaultCheckpoint: null,
  defaultSampler: 'euler',
  defaultScheduler: 'normal',
  defaultSteps: 20,
  defaultCfgScale: 7.0,
  defaultWidth: 512,
  defaultHeight: 512,
  defaultNegativePrompt: '',
  defaultPagEnabled: false,
  defaultPagScale: 3.0,
  defaultFreeUEnabled: false,
  defaultFreeUB1: 1.1,
  defaultFreeUB2: 1.2,
  defaultFreeUS1: 0.9,
  defaultFreeUS2: 0.2,
  isDirty: false,

  setLanguage: (language) => set({ language, isDirty: true }),
  setTheme: (theme) => set({ theme, isDirty: true }),
  setDefaultCheckpoint: (checkpoint) => set({ defaultCheckpoint: checkpoint, isDirty: true }),
  setDefaultSampler: (sampler) => set({ defaultSampler: sampler, isDirty: true }),
  setDefaultScheduler: (scheduler) => set({ defaultScheduler: scheduler, isDirty: true }),
  setDefaultSteps: (steps) => set({ defaultSteps: steps, isDirty: true }),
  setDefaultCfgScale: (cfgScale) => set({ defaultCfgScale: cfgScale, isDirty: true }),
  setDefaultWidth: (width) => set({ defaultWidth: width, isDirty: true }),
  setDefaultHeight: (height) => set({ defaultHeight: height, isDirty: true }),
  setDefaultNegativePrompt: (prompt) => set({ defaultNegativePrompt: prompt, isDirty: true }),
  setDefaultPagEnabled: (enabled) => set({ defaultPagEnabled: enabled, isDirty: true }),
  setDefaultPagScale: (scale) => set({ defaultPagScale: scale, isDirty: true }),
  setDefaultFreeUEnabled: (enabled) => set({ defaultFreeUEnabled: enabled, isDirty: true }),
  setDefaultFreeUB1: (b1) => set({ defaultFreeUB1: b1, isDirty: true }),
  setDefaultFreeUB2: (b2) => set({ defaultFreeUB2: b2, isDirty: true }),
  setDefaultFreeUS1: (s1) => set({ defaultFreeUS1: s1, isDirty: true }),
  setDefaultFreeUS2: (s2) => set({ defaultFreeUS2: s2, isDirty: true }),
  loadFromServer: (settings) =>
    set({
      language: settings.language,
      theme: settings.theme,
      defaultCheckpoint: settings.default_checkpoint,
      defaultSampler: settings.default_sampler,
      defaultScheduler: settings.default_scheduler,
      defaultSteps: settings.default_steps,
      defaultCfgScale: settings.default_cfg_scale,
      defaultWidth: settings.default_width,
      defaultHeight: settings.default_height,
      defaultNegativePrompt: settings.default_negative_prompt,
      defaultPagEnabled: settings.default_pag?.enabled ?? false,
      defaultPagScale: settings.default_pag?.scale ?? 3.0,
      defaultFreeUEnabled: settings.default_freeu?.enabled ?? false,
      defaultFreeUB1: settings.default_freeu?.b1 ?? 1.1,
      defaultFreeUB2: settings.default_freeu?.b2 ?? 1.2,
      defaultFreeUS1: settings.default_freeu?.s1 ?? 0.9,
      defaultFreeUS2: settings.default_freeu?.s2 ?? 0.2,
      isDirty: false,
    }),
  markClean: () => set({ isDirty: false }),
}));
